"use client"

import {
  Button,
  ButtonGroup,
  Text,
  VStack,
  Box,
  Table,
  Icon,
  HStack,
  Badge,
  Spinner,
} from "@chakra-ui/react"
import { useState, useCallback, useRef, useEffect, useId } from "react"
import { FaUpload, FaFile, FaFolder } from "react-icons/fa"
import { LuCheck, LuX } from "react-icons/lu"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  TableFieldsService,
  TableRowsService,
  StorageService,
  ExtractorService,
} from "../../client"
import useCustomToast from "../../hooks/useCustomToast"
import { ProgressBar } from "@/components/ui/progress-bar"
import { EventSourcePolyfill } from "event-source-polyfill"

import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogActionTrigger,
} from "../ui/dialog"

import {
  FileUploadDropzone,
  FileUploadList,
  FileUploadRoot,
} from "@/components/ui/file-upload"

type UploadMode = "file" | "folder"

interface BulkUploadTableRowsProps {
  tableId: string
  disabled?: boolean
}

interface FileExtractionState {
  fileName: string
  status: "uploading" | "extracting" | "completed" | "failed"
  documentId?: string
  taskId?: string
  extractedData?: Record<string, unknown>
  error?: string
}

const BulkUploadTableRows = ({
  tableId,
  disabled = false,
}: BulkUploadTableRowsProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [uploadMode, setUploadMode] = useState<UploadMode>("file")
  const [fileStates, setFileStates] = useState<FileExtractionState[]>([])
  const [saving, setSaving] = useState(false)
  const [saveProgress, setSaveProgress] = useState(0)
  const eventSourcesRef = useRef<Map<string, EventSourcePolyfill>>(new Map())
  const folderInputId = useId()
  const folderInputRef = useRef<HTMLInputElement>(null)

  const { showSuccessToast, showErrorToast } = useCustomToast()
  const queryClient = useQueryClient()

  const { data: fieldData } = useQuery({
    queryKey: ["tableFields", tableId],
    queryFn: () => TableFieldsService.readTableFields({ tableId }),
    enabled: !!tableId,
  })

  const fields = fieldData?.data || []

  // SSE 연결 해제
  const disconnectAll = useCallback(() => {
    eventSourcesRef.current.forEach((es) => es.close())
    eventSourcesRef.current.clear()
  }, [])

  // 개별 파일 SSE 폴링
  const pollTaskResult = useCallback(
    (taskId: string, fileIndex: number) => {
      const token = localStorage.getItem("access_token")
      if (!token) return

      const url = `${import.meta.env.VITE_API_URL}/api/v1/extractor/result/${taskId}/stream`
      const es = new EventSourcePolyfill(url, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        heartbeatTimeout: 300000,
      })

      eventSourcesRef.current.set(taskId, es)

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          const isCompleted =
            data.status === "completed" ||
            data.status === "SUCCESS" ||
            (data.ready && data.successful)
          const isFailed =
            data.status === "failed" ||
            data.status === "FAILURE" ||
            data.status === "error" ||
            (data.ready && data.failed)

          if (isCompleted && data.result) {
            const extracted = data.result?.data || data.result
            setFileStates((prev) => {
              const updated = [...prev]
              updated[fileIndex] = {
                ...updated[fileIndex],
                status: "completed",
                extractedData: extracted as Record<string, unknown>,
              }
              return updated
            })
            es.close()
            eventSourcesRef.current.delete(taskId)
          } else if (isFailed) {
            setFileStates((prev) => {
              const updated = [...prev]
              updated[fileIndex] = {
                ...updated[fileIndex],
                status: "failed",
                error: data.error || data.message || "추출 실패",
              }
              return updated
            })
            es.close()
            eventSourcesRef.current.delete(taskId)
          }
        } catch {
          // ignore parse errors
        }
      }

      es.onerror = () => {
        setFileStates((prev) => {
          const updated = [...prev]
          updated[fileIndex] = {
            ...updated[fileIndex],
            status: "failed",
            error: "SSE 연결 오류",
          }
          return updated
        })
        es.close()
        eventSourcesRef.current.delete(taskId)
      }
    },
    []
  )

  // 파일 업로드 + 추출 시작
  const processFile = useCallback(
    async (file: File, fileIndex: number) => {
      // 1. 업로드
      setFileStates((prev) => {
        const updated = [...prev]
        updated[fileIndex] = { ...updated[fileIndex], status: "uploading" }
        return updated
      })

      try {
        const uploadResponse = await StorageService.uploadFiles({
          formData: {
            context_id: tableId,
            files: [file],
            use_schema: "true",
          },
        })

        const uploadResult = uploadResponse as Record<string, unknown>
        if (
          !uploadResult.documents ||
          !Array.isArray(uploadResult.documents) ||
          uploadResult.documents.length === 0
        ) {
          throw new Error("파일 업로드 실패")
        }

        const uploadedDoc = uploadResult.documents[0] as Record<string, unknown>
        const docId = String(uploadedDoc.id)

        setFileStates((prev) => {
          const updated = [...prev]
          updated[fileIndex] = {
            ...updated[fileIndex],
            status: "extracting",
            documentId: docId,
          }
          return updated
        })

        // 2. 추출 요청
        const reader = new FileReader()
        reader.onload = async () => {
          try {
            const response = (await ExtractorService.extractSchema({
              documentId: docId,
              requestBody: {
                file: {
                  name: file.name,
                  data: reader.result as string,
                },
                schema_fields: fields.map((field) => ({
                  name: field.field_name,
                  type: field.field_type,
                  description: field.description || field.field_name,
                })),
                user_id: localStorage.getItem("user_id"),
                table_id: tableId,
              },
            })) as Record<string, unknown>

            const taskId = response.task_id as string
            setFileStates((prev) => {
              const updated = [...prev]
              updated[fileIndex] = {
                ...updated[fileIndex],
                taskId,
              }
              return updated
            })

            // 3. SSE 폴링 시작
            pollTaskResult(taskId, fileIndex)
          } catch (err) {
            setFileStates((prev) => {
              const updated = [...prev]
              updated[fileIndex] = {
                ...updated[fileIndex],
                status: "failed",
                error:
                  err instanceof Error ? err.message : "추출 요청 실패",
              }
              return updated
            })
          }
        }

        reader.onerror = () => {
          setFileStates((prev) => {
            const updated = [...prev]
            updated[fileIndex] = {
              ...updated[fileIndex],
              status: "failed",
              error: "파일 읽기 실패",
            }
            return updated
          })
        }

        reader.readAsDataURL(file)
      } catch (err) {
        setFileStates((prev) => {
          const updated = [...prev]
          updated[fileIndex] = {
            ...updated[fileIndex],
            status: "failed",
            error: err instanceof Error ? err.message : "업로드 실패",
          }
          return updated
        })
      }
    },
    [tableId, fields, pollTaskResult]
  )

  // 파일 선택 핸들러
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (!files.length) return

      const initialStates: FileExtractionState[] = files.map((f) => ({
        fileName: f.name,
        status: "uploading" as const,
      }))
      setFileStates(initialStates)

      // 모든 파일을 동시에 처리
      for (let i = 0; i < files.length; i++) {
        processFile(files[i], i)
      }
    },
    [processFile]
  )

  // 폴더 선택 핸들러
  const handleFolderSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files || files.length === 0) return

      // 지원하는 파일만 필터링
      const supportedExts = [".pdf", ".xlsx", ".csv", ".doc", ".docx", ".txt"]
      const filteredFiles = Array.from(files).filter((file) => {
        const ext = "." + file.name.split(".").pop()?.toLowerCase()
        return supportedExts.includes(ext)
      })

      if (filteredFiles.length === 0) {
        showErrorToast("지원되는 파일이 없습니다. (PDF, XLSX, CSV, DOC, DOCX, TXT)")
        return
      }

      handleFilesSelected(filteredFiles)
    },
    [handleFilesSelected, showErrorToast]
  )

  // 단일 추출 데이터를 매핑하여 저장하는 헬퍼
  const mapAndSaveRow = async (rowData: Record<string, unknown>) => {
    const mapped: Record<string, unknown> = {}
    for (const field of fields) {
      const value = rowData[field.field_name]
      mapped[field.field_name] =
        value !== undefined && value !== null && value !== ""
          ? value
          : "N/A"
    }
    await TableRowsService.createTableRow({
      tableId,
      requestBody: { data: mapped },
    })
  }

  // 완료된 결과 일괄 저장
  const handleSaveAll = async () => {
    const completedFiles = fileStates.filter(
      (f) => f.status === "completed" && f.extractedData
    )
    if (!completedFiles.length) return

    setSaving(true)
    setSaveProgress(0)

    try {
      // 먼저 모든 저장 대상 데이터를 수집 (배열 응답 펼치기)
      const allRows: Record<string, unknown>[] = []
      for (const file of completedFiles) {
        const extracted = file.extractedData!

        // data 키 안에 있으면 벗기기
        const innerData =
          typeof extracted === "object" &&
          extracted !== null &&
          "data" in extracted
            ? extracted.data
            : extracted

        // data가 배열이면 각 항목을 별도 row로 추가
        if (Array.isArray(innerData)) {
          for (const item of innerData) {
            if (typeof item === "object" && item !== null) {
              allRows.push(item as Record<string, unknown>)
            }
          }
        } else if (typeof innerData === "object" && innerData !== null) {
          allRows.push(innerData as Record<string, unknown>)
        }
      }

      for (let i = 0; i < allRows.length; i++) {
        await mapAndSaveRow(allRows[i])
        setSaveProgress(Math.round(((i + 1) / allRows.length) * 100))
      }

      showSuccessToast(
        `${allRows.length}개 데이터가 저장되었습니다.`
      )

      // 초기화 및 닫기
      setFileStates([])
      setIsOpen(false)

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "openSearchDocs" &&
          typeof query.queryKey[1] === "object" &&
          (query.queryKey[1] as { tableId: string }).tableId === tableId,
      })
    } catch (err) {
      showErrorToast("일부 데이터 저장 중 오류가 발생했습니다.")
      console.error(err)
    } finally {
      setSaving(false)
      setSaveProgress(0)
    }
  }

  const handleDialogClose = useCallback(() => {
    disconnectAll()
    setFileStates([])
    setSaving(false)
    setSaveProgress(0)
    setUploadMode("file")
    // 폴더 input 초기화
    const folderInput = document.getElementById(folderInputId) as HTMLInputElement
    if (folderInput) folderInput.value = ""
  }, [disconnectAll, folderInputId])

  useEffect(() => {
    return () => disconnectAll()
  }, [disconnectAll])

  const completedCount = fileStates.filter(
    (f) => f.status === "completed"
  ).length
  const failedCount = fileStates.filter((f) => f.status === "failed").length
  const processingCount = fileStates.filter(
    (f) => f.status === "uploading" || f.status === "extracting"
  ).length
  const allDone = fileStates.length > 0 && processingCount === 0

  const getStatusIcon = (status: FileExtractionState["status"]) => {
    switch (status) {
      case "uploading":
      case "extracting":
        return <Spinner size="xs" />
      case "completed":
        return <Icon as={LuCheck} color="green.500" />
      case "failed":
        return <Icon as={LuX} color="red.500" />
    }
  }

  const getStatusText = (state: FileExtractionState) => {
    switch (state.status) {
      case "uploading":
        return "업로드 중..."
      case "extracting":
        return "LLM 추출 중..."
      case "completed":
        return "추출 완료"
      case "failed":
        return state.error || "실패"
    }
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "lg" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (!open) handleDialogClose()
      }}
    >
      <DialogTrigger asChild>
        <Button my={4} disabled={disabled}>
          <Icon as={FaUpload} fontSize="16px" mr={1} />
          대량 등록
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>대량 등록 (LLM 기반 추출)</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="start" gap={4}>
            <Text fontSize="sm">
              여러 파일을 업로드하면 각 파일에서 LLM이 스키마에 맞게 데이터를
              추출합니다.
            </Text>

            {fileStates.length === 0 && (
              <>
                <HStack gap={2} w="full">
                  <Button
                    flex={1}
                    variant={uploadMode === "file" ? "solid" : "outline"}
                    onClick={() => setUploadMode("file")}
                    size="sm"
                  >
                    <Icon as={FaFile} mr={1} />
                    대량 파일 등록
                  </Button>
                  <Button
                    flex={1}
                    variant={uploadMode === "folder" ? "solid" : "outline"}
                    onClick={() => setUploadMode("folder")}
                    size="sm"
                  >
                    <Icon as={FaFolder} mr={1} />
                    대량 폴더 등록
                  </Button>
                </HStack>

                {uploadMode === "file" ? (
                  <FileUploadRoot
                    maxW="xl"
                    alignItems="stretch"
                    maxFiles={50}
                    inputProps={{
                      accept: ".pdf,.xlsx,.csv,.doc,.docx,.txt",
                      multiple: true,
                      onChange: (e) => {
                        const files = e.target.files
                        if (files && files.length > 0) {
                          handleFilesSelected(Array.from(files))
                        }
                      },
                    }}
                  >
                    <FileUploadDropzone
                      label="파일을 드래그하거나 클릭해서 업로드하세요"
                      description="지원 형식: PDF, XLSX, CSV, DOC, DOCX, TXT (여러 파일 선택 가능)"
                    />
                    <FileUploadList showSize={true} />
                  </FileUploadRoot>
                ) : (
                  <Box w="full">
                    <input
                      type="file"
                      onChange={handleFolderSelected}
                      style={{ display: "none" }}
                      id={folderInputId}
                      ref={folderInputRef}
                      // @ts-expect-error webkitdirectory is not in the type
                      webkitdirectory=""
                      directory=""
                    />
                    <Box
                      borderWidth={2}
                      borderStyle="dashed"
                      borderColor="gray.200"
                      borderRadius="lg"
                      p={8}
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ borderColor: "blue.300", bg: "blue.50" }}
                      transition="all 0.2s"
                      onClick={() => {
                        const input = document.getElementById(folderInputId) as HTMLInputElement
                        if (input) {
                          input.setAttribute("webkitdirectory", "")
                          input.setAttribute("directory", "")
                          input.click()
                        }
                      }}
                    >
                      <VStack gap={2}>
                        <Icon as={FaFolder} w={10} h={10} color="gray.400" />
                        <Text fontWeight="medium">폴더를 선택하세요</Text>
                        <Text fontSize="xs" color="gray.500">
                          선택한 폴더 내의 지원 파일(PDF, XLSX, CSV, DOC, DOCX, TXT)을 자동으로 처리합니다
                        </Text>
                      </VStack>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {fileStates.length > 0 && (
              <>
                <HStack gap={3}>
                  {completedCount > 0 && (
                    <Badge colorPalette="green">완료 {completedCount}</Badge>
                  )}
                  {processingCount > 0 && (
                    <Badge colorPalette="blue">처리 중 {processingCount}</Badge>
                  )}
                  {failedCount > 0 && (
                    <Badge colorPalette="red">실패 {failedCount}</Badge>
                  )}
                </HStack>

                <Box
                  maxH="350px"
                  overflowY="auto"
                  w="full"
                  borderWidth="1px"
                  borderRadius="md"
                >
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>파일명</Table.ColumnHeader>
                        <Table.ColumnHeader>상태</Table.ColumnHeader>
                        <Table.ColumnHeader>추출 필드 수</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {fileStates.map((state, idx) => (
                        <Table.Row key={idx}>
                          <Table.Cell>
                            <Text fontSize="sm" truncate maxW="250px">
                              {state.fileName}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <HStack gap={2}>
                              {getStatusIcon(state.status)}
                              <Text fontSize="xs" color="gray.600">
                                {getStatusText(state)}
                              </Text>
                            </HStack>
                          </Table.Cell>
                          <Table.Cell>
                            {state.extractedData ? (
                              <Text fontSize="sm">
                                {(() => {
                                  const ext = state.extractedData as Record<string, unknown>
                                  const inner = ext?.data ?? ext
                                  if (Array.isArray(inner)) {
                                    return `${inner.length}개 문서`
                                  }
                                  return typeof inner === "object" && inner !== null
                                    ? `${Object.keys(inner).length}개 필드`
                                    : "-"
                                })()}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color="gray.400">
                                -
                              </Text>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>

                {saving && (
                  <Box w="full">
                    <Text fontSize="xs" color="gray.600">
                      저장 중... {saveProgress}%
                    </Text>
                    <Box mt={1}>
                      <ProgressBar
                        value={saveProgress}
                        max={100}
                        style={{ width: "100%" }}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <ButtonGroup>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={saving || processingCount > 0}
              >
                취소
              </Button>
            </DialogActionTrigger>
            <Button
              onClick={handleSaveAll}
              colorScheme="blue"
              loading={saving}
              disabled={!allDone || completedCount === 0 || saving}
            >
              {completedCount > 0
                ? `${completedCount}개 저장`
                : "저장"}
            </Button>
          </ButtonGroup>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default BulkUploadTableRows
